import { ElementType, MouseEvent, ReactElement, ReactNode, useState } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import type {
  SxProps,
  Theme,
  TooltipProps,
  TypographyProps,
} from "@mui/material";

/** 팝업 본문 최대 폭(px). MUI Tooltip 기본값 300 을 덮어쓴다. */
const POPUP_MAX_WIDTH = 320;

/** 팝업 본문 최대 높이(px). 넘기면 팝업 안에서 세로로 스크롤한다. */
const POPUP_MAX_HEIGHT = 240;

/**
 * 트리거에서 팝업으로 포인터를 옮기는 동안 팝업이 닫히지 않게 하는 유예(ms).
 * MUI 기본 `leaveDelay` 는 0 이라 이 값을 주지 않으면 팝업 위로 마우스를 올릴 수 없다.
 */
const POINTER_TRANSFER_GRACE_MS = 150;

/** 세 형태가 공통으로 받는 prop. */
interface OverflowTooltipCommonProps {
  /** 이 컴포넌트가 그리는 요소(텍스트 형태면 Typography, 래핑 형태면 wrapper)에 적용할 스타일. */
  sx?: SxProps<Theme>;
  /** 팝업이 열리는 방향. 좌측 고정 사이드바 기준 기본값은 오른쪽이다. */
  placement?: TooltipProps["placement"];
}

/** 텍스트 형태(말줄임 Typography 를 직접 그리는 형태)가 공통으로 받는 prop. */
interface OverflowTooltipTextCommonProps extends OverflowTooltipCommonProps {
  /** 그릴 Typography 의 variant. */
  variant?: TypographyProps["variant"];
  /**
   * 그릴 엘리먼트.
   * 생략하면 MUI 가 `variant` 에 대응하는 태그를 쓴다(`h5` → `<h5>`, `subtitle2` → `<h6>`) —
   * 헤딩 의미를 잃지 않도록 기본 매핑을 덮어쓰지 않는다.
   */
  component?: ElementType;
  /** 텍스트 형태는 감싸지 않는다. */
  children?: never;
  /** 측정 대상이 자기 자신이므로 자손 선택자를 받지 않는다. */
  childSelector?: never;
}

/**
 * 문자열 텍스트 형태 — 팝업 본문의 기본값이 표시 문자열 자신이다.
 * 표시 값과 본문이 달라야 할 때만 `popupText` 를 준다.
 *
 * 이 형태로 갈리는 조건은 `text` 의 **정적 타입이 `string`** 인 것이다. 런타임에 문자열이
 * 들어오는지가 아니다 — `string | undefined`·`string | null`·`number` 는 `ReactNode` 로
 * 흡수돼 아래 노드 형태가 되고, 그쪽은 `popupText` 가 필수다. 그런 값을 이 형태로 쓰려면
 * 호출부에서 `?? ""`·`String(...)` 등으로 `string` 까지 좁힌다.
 */
interface OverflowTooltipStringTextProps
  extends OverflowTooltipTextCommonProps {
  /** 표시할 문자열. 그대로 팝업 본문이 된다. */
  text: string;
  /** 표시 값과 다른 팝업 본문(예: 원본 바이트 수 병기). */
  popupText?: string;
}

/**
 * 노드 텍스트 형태 — 표시 노드는 팝업 본문으로 복제할 수 없으므로(노드가 이미 품은 팝업과
 * 중첩된다) `popupText` 가 필수다. 없으면 팝업이 영영 열리지 않으니 타입이 이를 막는다.
 *
 * 정적 타입이 `string` 이 아닌 표시값은 전부 여기로 온다 — 엘리먼트뿐 아니라
 * `string | undefined`·`string | null`·`number` 같은 옵셔널·널러블·숫자 표시값도 포함된다.
 */
interface OverflowTooltipNodeTextProps extends OverflowTooltipTextCommonProps {
  /** 표시할 노드. */
  text: ReactNode;
  /** 팝업 본문. 노드는 본문이 될 수 없어 필수다. */
  popupText: string;
}

/**
 * 래핑 형태 — 넘긴 요소를 감싸 측정한다. 이 컴포넌트가 그리는 텍스트가 없으므로
 * `popupText` 가 필수다.
 */
interface OverflowTooltipWrapProps extends OverflowTooltipCommonProps {
  /** 감싸서 측정할 요소. */
  children: ReactElement;
  /** 팝업 본문. 그릴 텍스트가 없어 필수다. */
  popupText: string;
  /**
   * `children` 안에서 측정할 자손의 CSS 선택자.
   * 생략하면 감싼 요소 자신을 측정한다.
   */
  childSelector?: string;
  /** 래핑 형태는 텍스트를 직접 그리지 않는다. */
  text?: never;
  /** 그리는 Typography 가 없으므로 variant 를 받지 않는다. */
  variant?: never;
  /** 그리는 Typography 가 없으므로 component 를 받지 않는다. */
  component?: never;
}

/**
 * `OverflowTooltip` 이 받는 세 형태. 어느 형태든 **본문 prop 의 존재가 타입으로 보장된다** —
 * 정적 타입이 `string` 인 표시값이면 그 값 자신이, 그 외에는 `popupText` 가 본문이다.
 *
 * 보장되는 것은 존재까지다. 본문이 비어있지 않음은 타입이 막지 못한다 — `popupText=""` 나
 * 공백뿐인 `text` 는 컴파일을 통과하고, 런타임 `hasContent` 가 팝업을 열지 않는 쪽으로 막는다.
 */
export type OverflowTooltipProps =
  | OverflowTooltipStringTextProps
  | OverflowTooltipNodeTextProps
  | OverflowTooltipWrapProps;

// 소수점 아래 오차 — 글자 폭이 칸보다 이만큼 넘지 않으면 말줄임이 생기지 않는다
const SUBPIXEL_EPSILON_PX = 0.01;

/**
 * 잘렸는가 — `scrollWidth`·`clientWidth` 는 정수로 반올림돼 1 px 안쪽으로 넘친 글자(말줄임은 이미 보인다)를 놓친다.
 * 그래서 글자 내용의 실제 폭(소수)을 칸의 안쪽 폭(소수)과 한 번 더 비교한다.
 */
function isOverflowing(element: Element | null): boolean {
  if (element === null) return false;
  if (element.scrollWidth > element.clientWidth) return true;
  if (!(element instanceof HTMLElement) || element instanceof HTMLInputElement) {
    return false;
  }
  const style = getComputedStyle(element);
  const innerWidth =
    element.getBoundingClientRect().width -
    parseFloat(style.paddingLeft) -
    parseFloat(style.paddingRight) -
    parseFloat(style.borderLeftWidth) -
    parseFloat(style.borderRightWidth);
  const range = document.createRange();
  range.selectNodeContents(element);
  // 레이아웃이 없는 환경(jsdom)에는 범위의 상자가 없다
  if (typeof range.getBoundingClientRect !== "function") return false;
  return range.getBoundingClientRect().width > innerWidth + SUBPIXEL_EPSILON_PX;
}

/**
 * 말줄임된 텍스트의 전체 내용을 hover 팝업으로 보여준다 — **실제로 잘렸을 때만** 연다.
 *
 * 판정은 포인터가 들어오거나 위에서 움직이는 시점에 `scrollWidth > clientWidth` 로 실측한다 — 메뉴·입력칸이
 * 닫히며 포인터가 이미 글자 위에 있으면 들어오는 시점(mouseenter)이 없다. 사이드바 패널은
 * 접힐 때 폭이 줄지 않고 `display: none` 으로 사라지므로, 마운트 시점에 재서 캐시하면
 * 0/0 을 "안 잘림" 으로 오판한다.
 *
 * 세 가지 형태로 쓴다 — 어느 쪽이든 본문 prop 의 존재가 시그니처로 보장된다.
 * - `text` 에 정적 타입이 `string` 인 값만 주면 말줄임 `Typography` 를 그리고 그 문자열이
 *   팝업 본문이 된다. 본문만 따로 지정하려면 `popupText` 를 함께 준다.
 *   옵셔널·널러블·숫자 표시값(`string | undefined`·`string | null`·`number`)은 이 형태가
 *   아니라 아래 노드 형태로 떨어지므로 `popupText` 가 필요하다.
 * - `text` 에 노드를 주면 `popupText` 가 필수다 — 표시 노드를 본문으로 복제하면 노드가
 *   이미 품고 있는 팝업과 중첩된다.
 * - `children` 을 주면 그것을 감싸고, `childSelector` 로 지목한 자손을 측정한다
 *   (측정 대상이 감싼 요소 자신이 아니라 안쪽 `input` 등인 경우). 이때도 `popupText` 가 필수다.
 *
 * 시그니처가 보장하는 것은 본문 prop 의 **존재**이지 본문이 비어있지 않음이 아니다 —
 * 빈 문자열·공백뿐인 본문은 컴파일을 통과하므로, 잘렸더라도 본문이 비면 팝업을 열지 않는다.
 *
 * 팝업 본문 prop 이 `title` 이 아니라 `popupText` 인 이유: 이 레포의 자체 컴포넌트에서
 * `title` 은 화면에 그려지는 제목이고(`ModalHeader`·`SideMainHeader`·`DataTitle`),
 * 말줄임 대비로 쓰지 않기로 한 HTML 전역 `title` 속성과도 헷갈린다.
 *
 * 팝업은 hover 전용이다 — 키보드 포커스로 열리지 않는다.
 */
export function OverflowTooltip({
  popupText,
  text,
  variant,
  component,
  sx,
  childSelector,
  placement = "right",
  children,
}: OverflowTooltipProps) {
  const [truncated, setTruncated] = useState(false);

  /**
   * 팝업 본문 — `popupText` 를 줬으면 그것, 아니면 표시 문자열 자신이다.
   *
   * `popupText` 없이 타입을 통과하는 형태는 문자열 텍스트 형태 하나뿐이고 그 형태의 `text`
   * 는 정적 타입이 `string` 이다. 그래서 뒤쪽 `""` 갈래는 타입을 통과한 호출부에서는 도달하지
   * 않는다 — 타입 밖에서 온 값(`string` 으로 선언됐지만 런타임엔 없는 API 필드, JS 호출 등)에
   * 아래 `.trim()` 이 터지지 않게 남긴 방어값이다.
   */
  const content = popupText ?? (typeof text === "string" ? text : "");
  /**
   * 본문이 비었는지 — 이쪽은 죽은 코드가 아니다. `popupText=""` 나 공백뿐인 `text` 는 타입을
   * 통과하므로, 잘렸더라도 본문이 비면 팝업을 열지 않는 판단을 여기서 한다.
   */
  const hasContent = content.trim().length > 0;

  const measure = (event: MouseEvent<HTMLElement>) => {
    const target = childSelector
      ? event.currentTarget.querySelector(childSelector)
      : event.currentTarget;
    setTruncated(isOverflowing(target));
  };

  return (
    <Tooltip
      title={
        truncated && hasContent ? (
          <Box
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              maxHeight: POPUP_MAX_HEIGHT,
              overflowY: "auto",
            }}
          >
            {content}
          </Box>
        ) : (
          ""
        )
      }
      arrow
      placement={placement}
      leaveDelay={POINTER_TRANSFER_GRACE_MS}
      // 화살표 <span> 은 tooltip 요소의 자식이면서 음수 마진으로 박스 밖에 놓인다.
      // 여기에 overflow 를 주면 화살표가 잘리므로 스크롤은 본문 Box 가 맡는다.
      slotProps={{ tooltip: { sx: { maxWidth: POPUP_MAX_WIDTH } } }}
    >
      {children ? (
        <Box sx={sx} onMouseEnter={measure} onMouseOver={measure}>
          {children}
        </Box>
      ) : (
        <Typography
          variant={variant}
          // 생략 시 MUI 의 variant→태그 매핑을 그대로 쓴다. `component={undefined}` 를
          // 넘기면 오버로드가 깨지므로 값이 있을 때만 얹는다.
          {...(component ? { component } : {})}
          noWrap
          sx={sx}
          onMouseEnter={measure}
          onMouseOver={measure}
        >
          {text}
        </Typography>
      )}
    </Tooltip>
  );
}
